import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Banknote, Upload, CheckCircle2, AlertCircle, HelpCircle, X,
  ArrowDownCircle, ArrowUpCircle, Search, RefreshCw, Trash2, FileSpreadsheet,
  ChevronRight, Sparkles, Tag, Plus,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const STATUS_STYLE = {
  matched:   'bg-emerald-900/40 text-emerald-300 border-emerald-700',
  suggested: 'bg-amber-900/30 text-amber-300 border-amber-700',
  unmatched: 'bg-red-950/40 text-red-300 border-red-800',
};
const STATUS_ICON = {
  matched:   <CheckCircle2 size={11}/>,
  suggested: <HelpCircle size={11}/>,
  unmatched: <AlertCircle size={11}/>,
};

// Phase 2 — target type labels and colors
const TARGET_TYPE = {
  sales_order:         { label: 'Customer Invoice',  color: 'text-emerald-300 bg-emerald-950/30 border-emerald-800' },
  purchase_bill:       { label: 'Vendor Bill',       color: 'text-cyan-300 bg-cyan-950/30 border-cyan-800' },
  loan_disbursement:   { label: 'Loan Disbursement', color: 'text-violet-300 bg-violet-950/30 border-violet-800' },
  loan_payment:        { label: 'Loan EMI / Payment',color: 'text-orange-300 bg-orange-950/30 border-orange-800' },
  od_draw:             { label: 'OD Draw',           color: 'text-rose-300 bg-rose-950/30 border-rose-800' },
  od_repay:            { label: 'OD Repayment',      color: 'text-teal-300 bg-teal-950/30 border-teal-800' },
  credit_card_payment: { label: 'Credit Card Bill',  color: 'text-fuchsia-300 bg-fuchsia-950/30 border-fuchsia-800' },
  expense_classify:    { label: 'Classified Expense',color: 'text-amber-300 bg-amber-950/30 border-amber-800' },
  income_classify:     { label: 'Classified Income', color: 'text-lime-300 bg-lime-950/30 border-lime-800' },
};
const targetLabel = (tt) => TARGET_TYPE[tt]?.label || tt;
const targetClass = (tt) => TARGET_TYPE[tt]?.color || 'text-zinc-300 bg-zinc-900 border-zinc-800';

/**
 * BankReconciliation — Phase 1 + Phase 2.
 * Phase 1: vendor bills + customer invoices.
 * Phase 2: loan disbursements, loan EMIs/payments, OD draws/repays, credit card bill payments.
 * Phase 3 (pending): auto-create expense entries for unmatched lines.
 */
export default function BankReconciliation() {
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState('');
  const [statements, setStatements] = useState([]);
  const [activeStatement, setActiveStatement] = useState(null);  // {statement, lines, account}
  const [showUpload, setShowUpload] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadAccounts = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/bank-reco/accounts`, { withCredentials: true });
      setAccounts(r.data || []);
      if (!accountId && r.data?.[0]) setAccountId(r.data[0].id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load bank accounts');
    }
  }, [accountId]);

  const loadStatements = useCallback(async (accId) => {
    if (!accId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/bank-reco/statements?account_id=${accId}`, { withCredentials: true });
      setStatements(r.data || []);
    } catch (e) {
      toast.error('Failed to load statements');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);
  useEffect(() => { if (accountId) loadStatements(accountId); }, [accountId, loadStatements]);

  const openStatement = async (sid) => {
    try {
      const r = await axios.get(`${API}/bank-reco/statements/${sid}`, { withCredentials: true });
      setActiveStatement(r.data);
    } catch (e) {
      toast.error('Failed to load statement detail');
    }
  };

  const closeStatement = () => setActiveStatement(null);

  const refreshStatement = async (sid) => {
    await openStatement(sid);
    await loadStatements(accountId);
  };

  return (
    <div className="p-6 space-y-4" data-testid="bank-reco-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Banknote className="text-cyan-400" size={22}/>
          <div>
            <h1 className="text-2xl font-bold text-zinc-100">Bank Reconciliation</h1>
            <p className="text-zinc-500 text-sm">Upload statements · auto-match against vendor bills + customer invoices</p>
          </div>
        </div>
        <button
          onClick={() => setShowUpload(true)}
          data-testid="upload-statement-btn"
          disabled={!accountId}
          className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-black px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-40">
          <Upload size={14}/> Upload Statement
        </button>
      </div>

      {/* Account picker */}
      <div className="flex flex-wrap gap-2" data-testid="account-picker">
        {accounts.map(a => (
          <button
            key={a.id}
            onClick={() => setAccountId(a.id)}
            data-testid={`account-pick-${a.id}`}
            className={`text-xs px-3 py-1.5 rounded-lg border inline-flex items-center gap-1.5 ${accountId === a.id ? 'bg-cyan-500 text-black border-cyan-500 font-semibold' : 'bg-zinc-900 text-zinc-400 border-zinc-800 hover:text-zinc-200'}`}>
            <Banknote size={11}/> {a.name}
            {a.account_type === 'od' && <span className="text-[9px] px-1 py-0.5 rounded bg-amber-900/40 text-amber-300 ml-1">OD</span>}
          </button>
        ))}
      </div>

      {/* Statements list */}
      {loading ? (
        <div className="text-zinc-500 text-sm py-8 text-center">Loading statements…</div>
      ) : statements.length === 0 ? (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-10 text-center" data-testid="empty-state">
          <FileSpreadsheet className="mx-auto text-zinc-700 mb-3" size={40}/>
          <h3 className="text-zinc-300 text-sm font-semibold mb-1">No statements uploaded yet</h3>
          <p className="text-zinc-500 text-xs mb-4">Drop your HDFC export (Excel or CSV). We'll auto-match each line to open vendor bills or customer invoices.</p>
          <button onClick={() => setShowUpload(true)} className="inline-flex items-center gap-1.5 bg-cyan-500 text-black px-3 py-1.5 rounded text-xs font-semibold">
            <Upload size={11}/> Upload your first statement
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-2" data-testid="statements-list">
          {statements.map(s => <StatementCard key={s.id} statement={s} onOpen={() => openStatement(s.id)} />)}
        </div>
      )}

      {showUpload && (
        <UploadModal
          accounts={accounts}
          defaultAccountId={accountId}
          onClose={() => setShowUpload(false)}
          onUploaded={(r) => {
            toast.success(`Imported ${r.lines_imported} lines · ${r.auto_matched} matched · ${r.suggested} suggested · ${r.unmatched} unmatched`);
            setShowUpload(false);
            loadStatements(accountId);
          }}
        />
      )}

      {activeStatement && (
        <ReviewPanel
          payload={activeStatement}
          onClose={closeStatement}
          onChange={() => refreshStatement(activeStatement.statement.id)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Statement card (list row)
// ─────────────────────────────────────────────────────────────────────────
function StatementCard({ statement, onOpen }) {
  const matched = statement.matched_count || 0;
  const suggested = statement.suggested_count || 0;
  const unmatched = statement.unmatched_count || 0;
  const total = statement.total_lines || 0;
  const pct = total ? Math.round((matched / total) * 100) : 0;

  return (
    <div
      onClick={onOpen}
      data-testid={`statement-card-${statement.id}`}
      className="bg-zinc-900 border border-zinc-800 hover:border-cyan-700 rounded-xl p-4 cursor-pointer transition-colors">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <FileSpreadsheet className="text-cyan-400" size={14}/>
            <span className="text-zinc-100 text-sm font-semibold truncate">{statement.filename}</span>
          </div>
          <div className="text-xs text-zinc-500">
            {statement.period_start} → {statement.period_end} · {total} lines · uploaded {new Date(statement.uploaded_at).toLocaleDateString()}
          </div>
        </div>

        <div className="flex items-center gap-2 text-xs flex-wrap">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border bg-emerald-900/40 text-emerald-300 border-emerald-700">
            <CheckCircle2 size={11}/> {matched} matched
          </span>
          {suggested > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border bg-amber-900/30 text-amber-300 border-amber-700">
              <HelpCircle size={11}/> {suggested} suggested
            </span>
          )}
          {unmatched > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border bg-red-950/40 text-red-300 border-red-800">
              <AlertCircle size={11}/> {unmatched} unmatched
            </span>
          )}
          <span className="text-zinc-500">{pct}% reconciled</span>
          <ChevronRight size={14} className="text-zinc-600"/>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 text-xs">
        <Mini label="Credits (in)" val={fmt(statement.total_credit)} color="text-emerald-300"/>
        <Mini label="Debits (out)" val={fmt(statement.total_debit)} color="text-red-300"/>
        <Mini label="Net" val={fmt((statement.total_credit || 0) - (statement.total_debit || 0))}/>
        <Mini label="Lines" val={total}/>
      </div>
    </div>
  );
}

function Mini({ label, val, color = 'text-zinc-200' }) {
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5">
      <div className="text-[10px] text-zinc-500 uppercase tracking-wide">{label}</div>
      <div className={`font-mono text-xs ${color}`}>{val}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Upload modal
// ─────────────────────────────────────────────────────────────────────────
function UploadModal({ accounts, defaultAccountId, onClose, onUploaded }) {
  const [accId, setAccId] = useState(defaultAccountId || (accounts[0]?.id || ''));
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const submit = async () => {
    if (!file) return toast.error('Pick a file first');
    if (!accId) return toast.error('Pick a bank account first');
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('account_id', accId);
      fd.append('file', file);
      const r = await axios.post(`${API}/bank-reco/upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onUploaded(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="upload-modal">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl w-full max-w-md p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-zinc-100 text-base font-bold flex items-center gap-2">
            <Upload size={16} className="text-cyan-400"/> Upload Bank Statement
          </h2>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-300" data-testid="upload-close-btn">
            <X size={16}/>
          </button>
        </div>

        <label className="text-xs text-zinc-400 mb-1 block">Bank Account</label>
        <select
          value={accId}
          onChange={e => setAccId(e.target.value)}
          data-testid="upload-account-select"
          className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100 mb-3">
          {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>

        <label className="text-xs text-zinc-400 mb-1 block">Statement File (Excel .xlsx / CSV)</label>
        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={e => { e.preventDefault(); }}
          onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) setFile(f); }}
          data-testid="upload-dropzone"
          className="border-2 border-dashed border-zinc-700 hover:border-cyan-700 rounded-lg p-6 text-center cursor-pointer mb-3 transition-colors">
          <FileSpreadsheet className="mx-auto text-zinc-600 mb-2" size={32}/>
          {file ? (
            <div>
              <div className="text-zinc-200 text-sm font-medium">{file.name}</div>
              <div className="text-zinc-500 text-xs">{(file.size / 1024).toFixed(1)} KB</div>
            </div>
          ) : (
            <div>
              <div className="text-zinc-300 text-sm">Drop your statement here</div>
              <div className="text-zinc-500 text-xs">or click to browse</div>
            </div>
          )}
          <input
            type="file"
            ref={inputRef}
            data-testid="upload-file-input"
            accept=".xlsx,.xls,.csv,application/vnd.ms-excel,text/html"
            className="hidden"
            onChange={e => setFile(e.target.files?.[0])}
          />
        </div>

        <div className="bg-zinc-950 border border-zinc-800 rounded p-3 text-xs text-zinc-500 mb-3 leading-relaxed">
          <div className="text-zinc-300 font-medium mb-1 flex items-center gap-1">
            <Sparkles size={11} className="text-cyan-400"/> Smart auto-match
          </div>
          We'll detect <span className="text-zinc-300">Date · Narration · Withdrawal · Deposit</span> columns and match each line to your open vendor bills / customer invoices. Supports <span className="text-zinc-300">.xlsx · .xls · .csv</span> (incl. HDFC's HTML-disguised .xls). Duplicate uploads are blocked via file hash.
        </div>

        <div className="flex items-center gap-2 justify-end">
          <button onClick={onClose} className="text-xs text-zinc-400 hover:text-zinc-200 px-3 py-1.5">Cancel</button>
          <button
            onClick={submit}
            disabled={busy || !file}
            data-testid="upload-submit-btn"
            className="bg-cyan-500 hover:bg-cyan-400 disabled:opacity-40 text-black text-xs font-semibold px-4 py-2 rounded inline-flex items-center gap-2">
            {busy ? <><RefreshCw size={11} className="animate-spin"/> Uploading…</> : <><Upload size={11}/> Upload & Match</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Review panel — 3-pane reconciliation surface
// ─────────────────────────────────────────────────────────────────────────
function ReviewPanel({ payload, onClose, onChange }) {
  const { statement, lines, account } = payload;
  const [selectedId, setSelectedId] = useState(lines[0]?.id || '');
  const [filter, setFilter] = useState('all'); // all|matched|suggested|unmatched

  const filteredLines = lines.filter(l => filter === 'all' || l.status === filter);
  const selected = lines.find(l => l.id === selectedId);

  const deleteStatement = async () => {
    if (!window.confirm('Delete this statement and reverse all confirmed payments?')) return;
    try {
      await axios.delete(`${API}/bank-reco/statements/${statement.id}`, { withCredentials: true });
      toast.success('Statement removed; payments reversed');
      onClose();
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex" data-testid="review-panel">
      <div className="bg-zinc-950 border-l border-zinc-800 ml-auto w-full md:w-[90%] max-w-7xl h-full flex flex-col">
        {/* Header */}
        <div className="border-b border-zinc-800 p-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex-1 min-w-0">
            <h2 className="text-zinc-100 text-base font-bold truncate flex items-center gap-2">
              <FileSpreadsheet size={16} className="text-cyan-400"/> {statement.filename}
            </h2>
            <div className="text-xs text-zinc-500">
              {account.name} · {statement.period_start} → {statement.period_end} · {statement.total_lines} lines
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={deleteStatement} data-testid="delete-statement-btn"
              className="text-xs text-red-400 hover:text-red-300 px-2 py-1 inline-flex items-center gap-1">
              <Trash2 size={11}/> Delete
            </button>
            <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200" data-testid="review-close-btn">
              <X size={18}/>
            </button>
          </div>
        </div>

        {/* Filter chips */}
        <div className="border-b border-zinc-800 p-3 flex items-center gap-2 flex-wrap text-xs">
          {[
            { key: 'all',       label: `All (${lines.length})` },
            { key: 'matched',   label: `Matched (${statement.matched_count})`,   style: STATUS_STYLE.matched },
            { key: 'suggested', label: `Suggested (${statement.suggested_count})`, style: STATUS_STYLE.suggested },
            { key: 'unmatched', label: `Unmatched (${statement.unmatched_count})`, style: STATUS_STYLE.unmatched },
          ].map(c => (
            <button
              key={c.key}
              onClick={() => setFilter(c.key)}
              data-testid={`filter-${c.key}`}
              className={`px-3 py-1 rounded border ${filter === c.key ? 'bg-cyan-500 text-black border-cyan-500 font-semibold' : (c.style || 'bg-zinc-900 text-zinc-400 border-zinc-800') + ' hover:text-zinc-100'}`}>
              {c.label}
            </button>
          ))}
        </div>

        {/* Body — split */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left: line list */}
          <div className="w-1/2 border-r border-zinc-800 overflow-y-auto" data-testid="line-list">
            {filteredLines.length === 0 ? (
              <div className="p-6 text-center text-zinc-500 text-sm">No lines in this filter.</div>
            ) : filteredLines.map(l => (
              <LineRow
                key={l.id}
                line={l}
                selected={l.id === selectedId}
                onClick={() => setSelectedId(l.id)}
              />
            ))}
          </div>

          {/* Right: detail + actions */}
          <div className="w-1/2 overflow-y-auto bg-zinc-900/30" data-testid="line-detail">
            {selected ? (
              <LineDetail line={selected} onChanged={onChange} />
            ) : (
              <div className="p-6 text-center text-zinc-500 text-sm">Pick a line on the left to review.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function LineRow({ line, selected, onClick }) {
  const isCredit = line.credit > 0;
  return (
    <div
      onClick={onClick}
      data-testid={`line-row-${line.id}`}
      className={`border-b border-zinc-800 p-3 cursor-pointer hover:bg-zinc-900 ${selected ? 'bg-cyan-950/30 border-l-2 border-l-cyan-500' : ''}`}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2 text-xs">
          <span className="text-zinc-500 font-mono">{line.date}</span>
          {isCredit
            ? <ArrowDownCircle size={12} className="text-emerald-400"/>
            : <ArrowUpCircle size={12} className="text-red-400"/>}
          <span className={`font-mono font-semibold ${isCredit ? 'text-emerald-300' : 'text-red-300'}`}>
            {fmt(isCredit ? line.credit : line.debit)}
          </span>
        </div>
        <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border ${STATUS_STYLE[line.status]}`}>
          {STATUS_ICON[line.status]} {line.status}
        </span>
      </div>
      <div className="text-xs text-zinc-300 line-clamp-2">{line.narration || '—'}</div>
      {line.reference && <div className="text-[10px] text-zinc-500 mt-0.5 font-mono">Ref: {line.reference}</div>}
    </div>
  );
}

function LineDetail({ line, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const isCredit = line.credit > 0;
  const amt = isCredit ? line.credit : line.debit;

  const confirm = async (target_type, target_id) => {
    setBusy(true);
    try {
      await axios.post(`${API}/bank-reco/lines/${line.id}/confirm`,
        { target_type, target_id },
        { withCredentials: true });
      toast.success('Match confirmed — payment recorded');
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Confirm failed');
    } finally { setBusy(false); }
  };

  const unmatch = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/bank-reco/lines/${line.id}/unmatch`, {}, { withCredentials: true });
      toast.success('Match reversed');
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Unmatch failed');
    } finally { setBusy(false); }
  };

  const search = async () => {
    try {
      const r = await axios.get(`${API}/bank-reco/lines/${line.id}/candidates?q=${encodeURIComponent(searchQ)}`,
        { withCredentials: true });
      setSearchResults(r.data || []);
    } catch (e) {
      toast.error('Search failed');
    }
  };

  return (
    <div className="p-4 space-y-3" data-testid="line-detail-pane">
      {/* Line summary */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            {isCredit
              ? <ArrowDownCircle className="text-emerald-400" size={16}/>
              : <ArrowUpCircle className="text-red-400" size={16}/>}
            <span className="text-zinc-100 font-semibold">{isCredit ? 'Money Received' : 'Money Paid'}</span>
          </div>
          <span className={`font-mono text-lg font-bold ${isCredit ? 'text-emerald-300' : 'text-red-300'}`}>
            {fmt(amt)}
          </span>
        </div>
        <div className="text-xs text-zinc-400 mb-1"><span className="text-zinc-500">Date:</span> {line.date}</div>
        <div className="text-xs text-zinc-400 mb-1"><span className="text-zinc-500">Narration:</span> {line.narration || '—'}</div>
        {line.reference && <div className="text-xs text-zinc-400"><span className="text-zinc-500">Reference:</span> <span className="font-mono">{line.reference}</span></div>}
      </div>

      {/* If confirmed, show "what was matched" + unmatch */}
      {line.confirmed && line.matched_to && (
        <div className="bg-emerald-950/30 border border-emerald-800 rounded-lg p-3" data-testid="confirmed-block">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-2 text-emerald-300 text-sm font-semibold">
              <CheckCircle2 size={14}/> Confirmed Match
            </div>
            <button
              onClick={unmatch}
              disabled={busy}
              data-testid="unmatch-btn"
              className="text-xs text-amber-300 hover:text-amber-200 inline-flex items-center gap-1">
              <RefreshCw size={11}/> Unmatch
            </button>
          </div>
          <div className="text-xs text-zinc-200">
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] mr-1.5 ${targetClass(line.matched_to.target_type)}`}>
              {targetLabel(line.matched_to.target_type)}
            </span>
            {line.matched_to.target_label}
          </div>
          {line.matched_to.target_invoice_number && (
            <div className="text-xs text-zinc-500 mt-0.5">Invoice: {line.matched_to.target_invoice_number}</div>
          )}
          <div className="text-[10px] text-zinc-500 mt-1">Confirmed by {line.matched_to.confirmed_by} · {line.matched_to.confirmed_at?.slice(0, 10)}</div>
        </div>
      )}

      {/* If not confirmed, show candidates + classify panel */}
      {!line.confirmed && (
        <>
          <div className="text-xs text-zinc-400 font-semibold uppercase tracking-wide mt-2">
            Auto-matched Candidates
          </div>
          {(line.candidates || []).length === 0 ? (
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 text-center text-xs text-zinc-500" data-testid="no-candidates">
              No matching invoices/bills found. Classify as an expense or income below ↓
            </div>
          ) : (
            <div className="space-y-2" data-testid="candidates-list">
              {(line.candidates || []).map(c => (
                <Candidate key={c.target_id} cand={c} onConfirm={() => confirm(c.target_type, c.target_id)} disabled={busy}/>
              ))}
            </div>
          )}

          {/* Manual search */}
          <div className="mt-3 pt-3 border-t border-zinc-800">
            <div className="text-xs text-zinc-400 font-semibold uppercase tracking-wide mb-2">Find Different Match</div>
            <div className="flex gap-2 mb-2">
              <input
                value={searchQ}
                onChange={e => setSearchQ(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && search()}
                data-testid="manual-search-input"
                placeholder={isCredit ? 'Customer · invoice · lender · OD…' : 'Vendor · bill · lender · card · OD…'}
                className="flex-1 bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-xs text-zinc-100"/>
              <button
                onClick={search}
                data-testid="manual-search-btn"
                className="bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs px-3 py-1.5 rounded inline-flex items-center gap-1">
                <Search size={11}/> Search
              </button>
            </div>
            {searchResults.length > 0 && (
              <div className="space-y-2" data-testid="search-results">
                {searchResults.map(r => (
                  <Candidate key={r.target_id} cand={r} onConfirm={() => confirm(r.target_type, r.target_id)} disabled={busy}/>
                ))}
              </div>
            )}
          </div>

          {/* Phase 3 — Classify as Expense / Income */}
          <ClassifyPanel
            line={line}
            isCredit={isCredit}
            disabled={busy}
            onConfirm={(category) =>
              confirm(isCredit ? 'income_classify' : 'expense_classify', category)
            }
          />
        </>
      )}
    </div>
  );
}

function Candidate({ cand, onConfirm, disabled }) {
  const score = cand.score;
  const scoreColor = score >= 85 ? 'text-emerald-300' : score >= 40 ? 'text-amber-300' : 'text-zinc-500';
  return (
    <div className="bg-zinc-900 border border-zinc-800 hover:border-cyan-700 rounded-lg p-3" data-testid={`candidate-${cand.target_id}`}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="text-xs text-zinc-100 font-semibold truncate">{cand.target_label}</div>
        {typeof score === 'number' && (
          <span className={`text-[10px] font-mono font-bold ${scoreColor}`}>{score}%</span>
        )}
      </div>
      <div className="flex items-center justify-between text-[11px] text-zinc-400 mb-1">
        <span className="inline-flex items-center gap-1.5">
          <span className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] ${targetClass(cand.target_type)}`}>
            {targetLabel(cand.target_type)}
          </span>
          {cand.target_invoice_number && <span className="text-zinc-500">{cand.target_invoice_number}</span>}
        </span>
        <span className="font-mono">{cand.target_outstanding > 0 ? `Outstanding: ${fmt(cand.target_outstanding)}` : `Limit: ${fmt(cand.target_amount)}`}</span>
      </div>
      {(cand.reasons || []).length > 0 && (
        <div className="text-[10px] text-zinc-500 flex flex-wrap gap-1 mb-2">
          {cand.reasons.map((r, i) => <span key={i} className="bg-zinc-950 border border-zinc-800 rounded px-1.5 py-0.5">{r}</span>)}
        </div>
      )}
      <button
        onClick={onConfirm}
        disabled={disabled}
        data-testid={`confirm-${cand.target_id}`}
        className="bg-cyan-500 hover:bg-cyan-400 disabled:opacity-40 text-black text-[11px] font-semibold px-3 py-1 rounded inline-flex items-center gap-1">
        <CheckCircle2 size={11}/> Confirm match
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Phase 3 — Classify as Expense / Income
// ─────────────────────────────────────────────────────────────────────────
function ClassifyPanel({ line, isCredit, disabled, onConfirm }) {
  const kind = isCredit ? 'income' : 'expense';
  const [categories, setCategories] = useState([]);
  const [picked, setPicked] = useState(line.suggested_category || '');
  const [customMode, setCustomMode] = useState(false);
  const [customText, setCustomText] = useState('');

  useEffect(() => {
    let active = true;
    axios.get(`${API}/bank-reco/categories?kind=${kind}`, { withCredentials: true })
      .then(r => { if (active) setCategories(r.data || []); })
      .catch(() => {});
    return () => { active = false; };
  }, [kind]);

  // If line has a suggested_category, preselect it
  useEffect(() => {
    if (line.suggested_category && !picked) setPicked(line.suggested_category);
  }, [line.suggested_category, picked]);

  const submit = () => {
    const category = customMode ? customText.trim() : picked;
    if (!category) {
      toast.error('Pick a category or enter a custom one');
      return;
    }
    onConfirm(category);
  };

  return (
    <div className="mt-3 pt-3 border-t border-zinc-800" data-testid="classify-panel">
      <div className="text-xs text-zinc-400 font-semibold uppercase tracking-wide mb-2 flex items-center gap-2">
        <Tag size={12}/>
        {isCredit ? 'Classify as Income' : 'Classify as Expense'}
      </div>

      {line.suggested_category && !customMode && (
        <div className="text-[10px] text-cyan-400 mb-2">
          <Sparkles size={10} className="inline -mt-0.5 mr-0.5"/> Smart suggestion based on narration
        </div>
      )}

      {!customMode ? (
        <select
          value={picked}
          onChange={e => setPicked(e.target.value)}
          data-testid="classify-category-select"
          className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-xs text-zinc-100 mb-2">
          <option value="">— pick a category —</option>
          {categories.map(c => (
            <option key={c.id} value={c.name}>{c.name}{c.seeded ? '' : ' ✨'}</option>
          ))}
        </select>
      ) : (
        <input
          value={customText}
          onChange={e => setCustomText(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && submit()}
          autoFocus
          data-testid="classify-custom-input"
          placeholder="Type custom category name…"
          className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-xs text-zinc-100 mb-2"/>
      )}

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <button
          onClick={() => { setCustomMode(m => !m); setCustomText(''); }}
          data-testid="classify-toggle-custom"
          className="text-[11px] text-zinc-400 hover:text-zinc-200 inline-flex items-center gap-1">
          {customMode ? <><X size={10}/> Cancel custom</> : <><Plus size={10}/> Add custom category</>}
        </button>
        <button
          onClick={submit}
          disabled={disabled || (!picked && !customText.trim())}
          data-testid="classify-confirm-btn"
          className={`text-[11px] font-semibold px-3 py-1 rounded inline-flex items-center gap-1 disabled:opacity-40 ${
            isCredit
              ? 'bg-lime-500 hover:bg-lime-400 text-black'
              : 'bg-amber-500 hover:bg-amber-400 text-black'
          }`}>
          <CheckCircle2 size={11}/>
          Create {isCredit ? 'Income' : 'Expense'} Entry
        </button>
      </div>
    </div>
  );
}
